import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import AngleDown from '@gef-ui/material-icons/AngleDown';
import AngleUp from '@gef-ui/material-icons/AngleUp';

import { ComponentLoader } from 'components';
import Switches from 'components/atoms/CustomSVGIcon/Switches';
import Agency from 'components/atoms/GefMui/OrganizationStructure/Agency';
import Channel from 'components/atoms/GefMui/OrganizationStructure/Channel';
import Manager from 'components/atoms/GefMui/OrganizationStructure/Manager';
import Ppz from 'components/atoms/GefMui/OrganizationStructure/Ppz';
import Region from 'components/atoms/GefMui/OrganizationStructure/Region';
import Seller from 'components/atoms/GefMui/OrganizationStructure/Seller';
import { AUTO_FILL_RULES, USER_ROLES } from 'constants/general';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import { useCollapsible } from 'features/common/Subheader/hooks';
import contactReleasing from 'features/views/campaignManagement/contactReleasing';
import { saveSidebarFormValuesCache } from 'features/views/campaignManagement/contactReleasing/actions';
import nominatedSellers from 'features/views/campaignManagement/nominatedSellers';
import { resetOtherSidebarFields } from 'features/views/campaignManagement/sidebar/actions';
import { CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME, FIELDS } from 'features/views/campaignManagement/sidebar/form';
import user from 'features/views/user';

const { processes } = contactReleasing.constants;

const SidebarManualAssignNew = ({ campaignChannel, isNominatedCampaign, campaignId }) => {
	const dispatch = useDispatch();
	const currentRole = useSelector(user.selectors.getCurrentRole);
	const selectedProcess = useSelector(contactReleasing.selectors.getSelectedProcess);
	const selected = selectedProcess === processes.MANUAL_ASSIGN;
	// initiallyOpen is based on selected - because of returning from detail, checking the cache
	const { visible, handleToggle } = useCollapsible({
		id: subheaders.campaignManagementSidebarManualAssign,
		initiallyOpen: selected,
	});

	const { mode: appTheme } = useColorScheme();

	const handleOnClick = useCallback(() => {
		handleToggle();
		dispatch(resetOtherSidebarFields(processes.MANUAL_ASSIGN));
		dispatch(
			contactReleasing.actions.setProcess({
				process: selected ? null : processes.MANUAL_ASSIGN,
			}),
		);
	}, [dispatch, handleToggle, selected]);

	const nominationPath = useSelector(nominatedSellers.selectors.getPath);

	useEffect(() => {
		isNominatedCampaign && dispatch(nominatedSellers.actions.fetchNominatedSellers(campaignId));
	}, [dispatch, isNominatedCampaign, campaignId]);

	const handleChange = useCallback(() => {
		dispatch(saveSidebarFormValuesCache());
	}, [dispatch]);

	const disabled = isNominatedCampaign && isNilOrEmpty(nominationPath.sellers);
	nominationPath.channels = isNominatedCampaign && campaignChannel ? [campaignChannel] : [];

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.campaignNominations}>
			<Stack spacing={1}>
				<Stack
					sx={{
						cursor: disabled ? 'default' : 'pointer',
						borderWidth: '1px',
						borderStyle: disabled ? 'dashed' : 'solid',
						borderColor: 'lightgrey',
						borderRadius: '4px',
						height: '40px',

						backgroundColor: (theme) =>
							appTheme === 'light'
								? selected
									? 'primary.shades.8p'
									: theme.palette.common.white
								: selected
								  ? 'primary.shades.30p'
								  : theme.palette.background[300],

						':hover': {
							borderColor: disabled ? 'default' : 'common.black',
						},

						alignItems: 'center',
						justifyContent: 'space-between',
					}}
					direction="row"
					onClick={!disabled && handleOnClick}
				>
					<Stack direction="row">
						<Stack
							direction="row"
							sx={{
								alignItems: 'center',
							}}
						>
							<IconButton
								sx={{
									cursor: disabled ? 'default' : 'pointer',
									fontSize: '15px',
									color: disabled ? 'grey.400' : 'info.main',
									ml: 0.5,
									mr: 1,
								}}
								disableRipple
							>
								<Switches />
							</IconButton>
							<Typography
								sx={{
									fontSize: '1rem',
									color: (theme) =>
										appTheme === 'light'
											? selected
												? theme.palette.text.primary
												: theme.palette.grey[600]
											: selected
											  ? theme.palette.text.primary
											  : theme.palette.grey[300],
									fontWeight: 400,
								}}
							>
								{getLabelMessage('campaignManagement:manualAssign')}
							</Typography>
						</Stack>
					</Stack>
					<IconButton
						sx={{
							cursor: disabled ? 'default' : 'pointer',
							fontSize: '15px',
							color: disabled ? 'grey.400' : 'info.main',
							mx: 0.5,
						}}
						disableRipple
					>
						{visible ? <AngleUp /> : <AngleDown />}
					</IconButton>
				</Stack>
				<Collapse in={visible}>
					<Stack spacing={1}>
						<Channel
							fieldName={FIELDS.channel}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
							customAllowedChannels={nominationPath.channels}
							type="sidebar"
							onChange={handleChange}
						/>
						<Region
							fieldName={FIELDS.region}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
							customAllowedRegions={nominationPath.regions}
							type="sidebar"
							onChange={handleChange}
						/>
						<Agency
							fieldName={FIELDS.agency}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.REGION_MANAGER)}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.REGION_MANAGER)}
							customAllowedAgencies={nominationPath.agencies}
							type="sidebar"
							onChange={handleChange}
						/>
						<Manager
							fieldName={FIELDS.manager}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.AGENCY_MANAGER)}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.AGENCY_MANAGER)}
							customAllowedManagers={nominationPath.managers}
							type="sidebar"
							onChange={handleChange}
						/>
						<Seller
							fieldName={FIELDS.seller}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.SELLER)}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.SELLER)}
							customAllowedSellers={nominationPath.sellers}
							type="sidebar"
							onChange={handleChange}
						/>
						<Ppz
							fieldName={FIELDS.ppz}
							formName={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME}
							autoSelect={false}
							customAllowedPpz={nominationPath.ppz}
							type="sidebar"
							onChange={handleChange}
						/>
					</Stack>
				</Collapse>
			</Stack>
		</ComponentLoader>
	);
};

SidebarManualAssignNew.propTypes = {
	campaignChannel: PropTypes.number,
	campaignId: PropTypes.number,
	isNominatedCampaign: PropTypes.bool,
};

export default SidebarManualAssignNew;
